import styles from './PerformanceTimeRange.module.scss';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import {
  SelectionTimeRange,
  TimeRange,
  setPerformanceTimeFilter,
} from '@customer/store/slices/globalSlice';
import { useCallback, useMemo } from 'react';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import Dropdown from '@cas/components/inputs/dropdown/Dropdown';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import { useTranslation } from 'react-i18next';

const usePerformanceTimeRangeOptions = (): DropdownItemProps<SelectionTimeRange>[] => {
  const { t } = useTranslation();

  const { getCustomTimeRangeText, getTimeRangeText } = useLocaleFormatter();

  const {
    performanceTimeRangeCustom: { timeRangeStart, timeRangeEnd },
  } = useAppSelector(state => state.global);

  return useMemo(() => {
    const valuesWithoutCustom: TimeRange[] = Object.values(SelectionTimeRange).filter(
      (value: SelectionTimeRange) => value !== SelectionTimeRange.Custom,
    );

    const items: DropdownItemProps<SelectionTimeRange>[] = valuesWithoutCustom.map(
      (value: TimeRange) => {
        return {
          key: value,
          value,
          stringValue: value,
          label: getTimeRangeText(value, t),
        };
      },
    );

    // Add a custom time range option if both start and end times have been set.
    if (timeRangeStart && timeRangeEnd) {
      const value = SelectionTimeRange.Custom;

      items.push({
        key: value,
        value,
        stringValue: value,
        label: getCustomTimeRangeText(timeRangeStart, timeRangeEnd),
      });
    }

    return items;
  }, [t, getTimeRangeText, getCustomTimeRangeText, timeRangeStart, timeRangeEnd]);
};

interface TimeRangeProps {
  disabled?: boolean;
}

export const PerformanceTimeRange = ({ disabled }: TimeRangeProps) => {
  const dispatch = useAppDispatch();

  const {
    performanceTimeRange,
    performanceTimeRangeCustom: { timeRangeStart, timeRangeEnd },
  } = useAppSelector(state => state.global);

  const items = usePerformanceTimeRangeOptions();

  const onChange = useCallback(
    (newValue: SelectionTimeRange) => {
      dispatch(setPerformanceTimeFilter(newValue));
    },
    [dispatch],
  );

  // Note: The VDS dropdown needs a new key whenever the custom time range changes or it fails to update the dropdown label.
  const renderKey = `custom-${timeRangeStart}-${timeRangeEnd}`;

  return (
    <div className={styles.timeSelect}>
      <Dropdown<SelectionTimeRange>
        key={renderKey}
        uid="performanceTimeRange"
        value={performanceTimeRange}
        disabled={disabled}
        items={items}
        onChange={onChange}
      />
    </div>
  );
};

export default PerformanceTimeRange;
